import {
  Chip,
  Collapse,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { memo, useState, type ReactNode } from 'react';

export interface NavGroupProps {
  readonly label: string;
  readonly icon?: ReactNode;
  readonly children: ReactNode;
  /** Drawer mini-variant — render children directly (icon-only), no group chrome. */
  readonly collapsed?: boolean;
  readonly defaultExpanded?: boolean;
  /** localStorage key suffix — remembers the expand/collapse choice across sessions. */
  readonly persistKey?: string;
  /**
   * Aggregate count bubbled onto the group header ONLY while the group is
   * collapsed, so a nested badge (e.g. the Leads new-count) isn't lost when
   * the group is closed.
   */
  readonly badgeContent?: number;
  /** aria-label for the bubbled badge. */
  readonly badgeLabel?: string;
}

const readPersisted = (key: string | undefined, fallback: boolean): boolean => {
  if (!key) return fallback;
  try {
    const v = window.localStorage.getItem(key);
    return v === null ? fallback : v === 'true';
  } catch {
    return fallback;
  }
};

/**
 * Collapsible sidebar group — a labeled toggle header over a nested `List`
 * of `NavItem`s (e.g. "Marketing" wrapping Campaigns + Leads inside the
 * Records section). The expand/collapse choice is persisted per
 * `persistKey` so it survives a reload.
 *
 * In the drawer's mini-variant (`collapsed`, the 64px icon rail) the group
 * chrome is dropped entirely and children render directly, icon-only, each
 * keeping its own badge — matching how a plain `NavItem` degrades in that
 * mode. There's no sensible "expand a nested group" affordance in a rail
 * that has no room for labels at all.
 *
 * When the group is collapsed (closed, drawer still expanded), `badgeContent`
 * bubbles onto the header as a trailing pill — mirroring `NavItem`'s own
 * expanded-mode badge treatment — so a hidden nested badge (Leads' unread
 * count) stays visible even when its parent group is closed.
 */
const NavGroupComponent = ({
  label,
  icon,
  children,
  collapsed = false,
  defaultExpanded = true,
  persistKey,
  badgeContent = 0,
  badgeLabel,
}: NavGroupProps): JSX.Element => {
  const storageKey = persistKey ? `qfly.nav.${persistKey}.expanded` : undefined;
  const [expanded, setExpanded] = useState(() => readPersisted(storageKey, defaultExpanded));

  // Drawer mini-variant: children are icon-only NavItems that keep their own
  // badges — render them directly, without the (label-bearing) group header.
  if (collapsed) {
    return <>{children}</>;
  }

  const toggle = (): void => {
    setExpanded((prev) => {
      const next = !prev;
      if (storageKey) {
        try {
          window.localStorage.setItem(storageKey, String(next));
        } catch {
          /* localStorage unavailable (private mode / quota) — ignore. */
        }
      }
      return next;
    });
  };

  const showBadge = !expanded && badgeContent > 0;
  const badgeAriaLabel = badgeLabel ?? `${badgeContent} new`;
  const badgeDisplay = badgeContent > 99 ? '99+' : badgeContent;

  const listId = `nav-group-${label.toLowerCase().replace(/\s+/g, '-')}`;

  return (
    <>
      <ListItemButton
        onClick={toggle}
        aria-expanded={expanded}
        aria-controls={listId}
        sx={{
          minHeight: 35,
          borderRadius: 1,
          px: 1.25,
          py: 0.875,
          color: (theme) => theme.palette.grey[500],
          // Touch-target floor (CLAUDE.md responsive rules) — same 35px
          // row-height risk and same fix as `NavItem.tsx`'s own copy of
          // this pattern (kept inline per-file rather than shared, matching
          // `client-success/touchTarget.ts`'s own precedent of a small,
          // duplicated constant over a cross-feature import).
          '@media (pointer: coarse)': { minHeight: 44 },
        }}
      >
        {icon ? (
          <ListItemIcon sx={{ minWidth: 36, mr: 1.125, justifyContent: 'center', color: 'inherit' }}>
            {icon}
          </ListItemIcon>
        ) : null}
        <ListItemText
          primary={label}
          sx={{ my: 0 }}
          primaryTypographyProps={{
            sx: { fontSize: 14, fontWeight: 500, lineHeight: '21px' },
          }}
        />
        {showBadge ? (
          <Chip
            label={badgeDisplay}
            color="error"
            size="small"
            aria-label={badgeAriaLabel}
            sx={{
              ml: 1,
              height: 14,
              '& .MuiChip-label': {
                fontSize: '10px',
                fontWeight: 700,
                lineHeight: 1,
                padding: '2px 6px',
              },
            }}
          />
        ) : null}
        {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
      </ListItemButton>
      {/*
       * No `pl` here — verified directly against the QFly-v2 Figma file
       * (node 453:3771): the group header and each child row ("Campaigns",
       * "Leads", etc. — see node 453:3789 vs. 453:3969/453:3958/453:3979/
       * 453:3959) share IDENTICAL `px-10px` container padding and the same
       * 16px-icon + 9px-gap slot before their text — they're flat siblings
       * in Figma, not actually indented relative to each other. A child is
       * visually "nested" only by being grouped under a collapsible header,
       * never by extra horizontal inset. An earlier `pl: 1.5` (12px) here
       * pushed every child's text 12px right of the group header's own
       * text — a real, visible misalignment a user caught by eye, since
       * both rows' `ListItemButton`s already use the identical `px: 1.25`
       * and `NavSection.tsx`'s `renderItem`/the group header both wrap
       * their icon in the same `ListItemIcon` sizing.
       */}
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <List component="div" disablePadding id={listId}>
          {children}
        </List>
      </Collapse>
    </>
  );
};

export const NavGroup = memo(NavGroupComponent);
